import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ticketActivitiesSet,
  ticketAttachmentsSet,
  ticketDocumentSet,
  ticketDocumentVersionsSet,
  ticketParticipantsSet,
  ticketReset,
  ticketStagesSet,
  ticketSummarySet,
  ticketUpdate,
} from '~/actions/ticket';
import { checkEsignatureIntegration } from '~/api/integrations';
import {
  checkEnvelope,
  checkESignatureLogin,
  downloadTicketDocument,
  downloadTicketDocumentWithoutActivityLog,
  getActivities,
  getTicketAttachments,
  getTicketDocumentVersions,
  getTicketParticipants,
  patchTicketStage,
  updateEnvelope,
  uploadNewDocumentVersion,
} from '~/api/tickets';
import {
  TICKET_DOCUMENT_VERSION_EDITOR_KEY_POLLING_INTERVAL,
  TICKET_DOCUMENT_VERSION_POLLING_MAX_COUNTER,
  WORKFLOW_TAB_INDEX,
} from '~/common/constants/ticket';
import { usePanels } from '~/common/constants/usePanels';
import AccessControl from '~/components/Shared/AccessControl/AccessControl';
import AppLayout from '~/components/Shared/AppLayout/AppLayout';
import { ActivityLogPanel } from '~/components/Shared/AppLayout/SidebarContent/ActivityLogs/ActivityLogPanel';
import { Comments } from '~/components/Shared/AppLayout/SidebarContent/Comments/Comments';
import { TicketIntakeForm as IntakeFormPanel } from '~/components/Shared/AppLayout/SidebarContent/IntakeForm/IntakeForm';
import {
  EcTab,
  EcTabList,
  EcTabPanel,
  EcTabs,
} from '~/components/Shared/EcTabs';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { EnvelopeStatusChip } from '~/components/Shared/EnvelopeStatusChip/EnvelopeStatusChip';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import TicketReviewEmptyStateIcon from '~/components/Shared/Icons/TicketReviewEmptyStateIcon';
import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box as BoxEds } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { ErrorPage } from '~/eds/src/components/ErrorPage/ErrorPage';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { PageSidebar } from '~/eds/src/components/PageSidebar/PageSidebar';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { Text as TextEds } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { EntityType } from '~/enums/EntityType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { FileMimeType } from '~/enums/FileMimeType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { PanelType } from '~/enums/PanelType';
import { QueryParamType } from '~/enums/QueryParamType';
import { TicketActivityType } from '~/enums/TicketActivityType';
import { TicketNegotiationStatus } from '~/enums/TicketNegotiationStatus';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { useAskAISidepanel } from '~/features/ask-anything/hooks/useAskAISidepanel';
import { GroupMembership } from '~/features/groups/Panels/GroupMembership';
import { TicketRecipients } from '~/features/ticket/stages/sign/recipients/TicketRecipients';
import { TurnTrackingPanel } from '~/features/turn-tracking/TurnTrackingPanel';
import { partyNameFromPartyIdAndParties } from '~/features/turn-tracking/utils';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useSearchParams } from '~/hooks/router';
import { useAsync } from '~/hooks/useAsync';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { usePermission } from '~/hooks/usePermission';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import {
  getDocumentVersion,
  getHasTemporaryViewAnyPermission,
} from '~/reducers/ticket';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import { TagType } from '~/redux/api/TagType';
import { actions, slices } from '~/redux/slices';
import { useAppDispatch, useAppSelector } from '~/rootState';
import { useRouting } from '~/routing/useRouting';
import * as toastTypes from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import Divider from '~/ui/components/Divider/Divider';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Icon from '~/ui/components/Icon/Icon';
import Text from '~/ui/components/Text/Text';
import {
  coerceFileType,
  downloadFile,
  getBasename,
  getFileIcon,
} from '~/utils/files';
import { copyToClipboard } from '~/utils/helper.utils';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';
import { removeFileNameExtension } from '~/utils/ticket';

import { useActivityLogUpdater } from '../shared/useActivityLogUpdater';
import { useAddTicketParticipantModal } from '../shared/useAddTicketParticipantModal';
import { getConstants } from '../TicketReviewerPage/constants';
import { testPhaseNonEmpty } from '../Workflow.utils';
import { TokenStatusMessage } from '../WorkflowTicketsPage/TokenStatusMessage/TokenStatusMessage';
import ESignatureSetupAlert from './ESignatureSetupAlert';
import LastModifiedBy from './LastModifiedBy';
import TicketCancelledWarning from './TicketCancelledWarning';
import TicketDocumentActions from './TicketDocumentActions/TicketDocumentActions';
import TicketDocumentNotices from './TicketDocumentNotices/TicketDocumentNotices';
import TicketHeader from './TicketHeader/TicketHeader';
import TicketInboundEmail from './TicketInboundEmail/TicketInboundEmail';
import { TicketParticipants } from './TicketParticipants/TicketParticipants';
import TicketReviewPhase from './TicketReviewPhase/TicketReviewPhase';
import TicketSigningPhase from './TicketSigningPhase/TicketSigningPhase';
import TicketUserItem from './TicketUserItem/TicketUserItem';
import { getTurnTrackingPartiesNames } from './utils';

const stagePermissions = {
  [TicketStageType.Edit]: TicketPermissionType.EditView,
  [TicketStageType.Review]: TicketPermissionType.ReviewView,
  [TicketStageType.Sign]: TicketPermissionType.SignView,
  [TicketStageType.Finalize]: TicketPermissionType.FinalizeView,
  [TicketStageType.Ticket]: TicketPermissionType.TicketView,
};

function LoadingContainer() {
  return (
    <Box p={30} sx={{ textAlign: 'center' }}>
      <LoadingSpinner size="medium" mode="quote" />
    </Box>
  );
}

function ErrorMessage({ ticketSummaryError, onTakeMeToTickets }) {
  const intl = useIntl();
  const { ERROR_MESSAGES } = getConstants(intl);
  const { preset, title, description } =
    ERROR_MESSAGES[ticketSummaryError?.response?.status] ??
    ERROR_MESSAGES['default'];

  return (
    <ErrorPage
      preset={preset}
      title={title}
      description={description}
      resolveActionText={intl.formatMessage({
        id: 'TicketsViewPage.takeMeToTickets',
        defaultMessage: 'Take me to Tickets',
        description: 'Button text to navigate to the tickets page.',
      })}
      resolveAction={{
        onClick: onTakeMeToTickets,
      }}
    />
  );
}

function TicketContent({
  ticket,
  viewStagePermission,
  isDocumentSectionLoading,
  isLoadingCurrentStage,
  isAskAnythingPreSigEnabled,
  versionId,
  isChatBotPanelVisible,
  turnTrackingHistoryData,
  turnTrackingPartiesData,
  isLoadingTurnTrackingHistory,
  isLoadingTurnTrackingParties,
  isLoadingPermission,
  onViewCycleTimeTracker,
  hasTicketInboundEmail,
  inboundEmailAddress,
  isEsignatureV2Enabled,
  documentActionsHandlers,
  documentActionsLoaders,
  pilotDocHandlerId,
  hasTemporaryViewAnyPermission,
  hasEsignatureV2Revamp,
  envelope,
  isLoadingEnvelope,
  defaultPanelActions,
  panels,
  onPanelClick,
  documentData,
  ticketCommentId,
  ticketEntity,
  panelWidth,
  onMentionRequestPermission,
  onCopyLink,
  ticketId,
  documentVersions,
  isSidebarExpanded,
  isAskAISidepanelVisible,
  askAISidepanelWidth,
  selectedPanel,
  onPanelUpdate,
  onSidebarClose,
  addParticipantModal,
  activeTabIndex,
  onActiveTabIndexChange,
  isPanelEnlarged,
}) {
  const intl = useIntl();
  const { TICKET_COMMENT_CHIP } = getConstants(intl);
  const hasGroupedTicketsAndDocsM2 = useFlag(FlagType.GroupedTicketsAndDocsM2);

  const activityLogPanelCustomActions = useMemo(
    () => ({
      intakeFormAction: () => onPanelUpdate(PanelType.IntakeForm),
    }),
    [onPanelUpdate],
  );
  const activityLogPanelVersions = useMemo(
    () => documentVersions ?? [],
    [documentVersions],
  );

  return (
    <AppLayout
      sidebar={
        <PageSidebar
          isExpanded={isSidebarExpanded}
          selectedPanel={selectedPanel}
          onClose={onSidebarClose}
          onUpdate={onPanelUpdate}
          topDistance="m"
          width={isAskAISidepanelVisible ? askAISidepanelWidth : undefined}
          panels={{
            ...(isAskAnythingPreSigEnabled
              ? {
                  [PanelType.AskAI]: {
                    action: {
                      disabled: !documentData,
                      ...panels[PanelType.AskAI],
                      onClick: onPanelClick,
                    },
                    component: <Layout />,
                  },
                }
              : {}),

            comments: {
              action: {
                ...panels[PanelType.Comments],
                onClick: onPanelClick,
              },
              component: (
                <Comments
                  activeCommentId={ticketCommentId}
                  entity={ticketEntity}
                  isCommentsEnlarged={isPanelEnlarged}
                  isExpanded={Boolean(ticketCommentId)}
                  chips={[TICKET_COMMENT_CHIP]}
                  onMention={onMentionRequestPermission}
                  onCopyLink={onCopyLink}
                  filterComment={(comment) =>
                    !comment.context.some((context) =>
                      [
                        EntityType.Highlight,
                        EntityType.EditorHighlight,
                      ].includes(context.type),
                    )
                  }
                />
              ),
            },
            ...{
              cycleTimeTracker: {
                action: {
                  ...panels[PanelType.CycleTimeTracker],
                  onClick: onPanelClick,
                },
                component: (
                  <TurnTrackingPanel
                    ticketId={ticketId}
                    workflowId={ticket.workflowId}
                  />
                ),
              },
            },
            activityLog: {
              action: {
                ...panels[PanelType.ActivityLogs],
                onClick: onPanelClick,
              },
              component: (
                <ActivityLogPanel
                  ticketId={ticketId}
                  customActions={activityLogPanelCustomActions}
                  versions={activityLogPanelVersions}
                />
              ),
            },
            ...(hasGroupedTicketsAndDocsM2
              ? {
                  groupMembership: {
                    action: {
                      ...panels[PanelType.GroupMembership],
                      onClick: onPanelClick,
                    },
                    component: (
                      <Panel
                        width="560px"
                        title={intl.formatMessage({
                          id: 'UsePanels.groupsTooltip',
                          defaultMessage: 'Groups',
                          description: 'Groups panel tooltip',
                        })}
                      >
                        <GroupMembership
                          resourceType="ticket"
                          resourceId={ticketId}
                        />
                      </Panel>
                    ),
                  },
                }
              : {}),
            intakeForm: {
              action: {
                ...panels[PanelType.IntakeForm],
                onClick: onPanelClick,
              },
              component: (
                <IntakeFormPanel
                  ticketId={ticketId}
                  title={intl.formatMessage({
                    id: 'TicketsViewPage.formInformation',
                    defaultMessage: 'Form Information',
                    description: 'Title for the form information panel.',
                  })}
                />
              ),
            },
          }}
        />
      }
    >
      <TicketHeader ticket={ticket} loading={false} />
      {isAskAnythingPreSigEnabled && (
        <AskAiStatusMessage
          ticketActivities={ticket.activities}
          versionId={versionId}
          isChatBotPanelVisible={isChatBotPanelVisible}
        />
      )}
      <AccessControl
        userPermissions={ticket.permissions}
        requiredPermission={viewStagePermission}
        renderNoAccess={() => {
          const headline = intl.formatMessage({
            id: 'TicketsViewPage.statusHeadline.noPermission',
            defaultMessage: 'You do not have permission to view this ticket.',
            description:
              'Status headline when user does not have permission to view a ticket.',
          });

          const subheadline = intl.formatMessage({
            id: 'TicketsViewPage.statusSubheadline.noPermission',
            defaultMessage:
              "Access has been restricted based on this ticket's workflow settings. Please contact your administrator if you believe you should have access.",
            description:
              'Status subheadline when user does not have permission to view a ticket based on workflow settings.',
          });

          return (
            <FlexLayout
              alignItems="center"
              flexDirection="column"
              mt="118px"
              space={10}
            >
              <TicketReviewEmptyStateIcon />
              <FlexLayout alignItems="center" flexDirection="column" space={2}>
                <Text color="gray-700" variant="l-dense-bold">
                  {headline}
                </Text>
                <Text color="gray-700" variant="xs-dense">
                  {subheadline}
                </Text>
              </FlexLayout>
            </FlexLayout>
          );
        }}
      >
        <EcTabs
          selectedIndex={activeTabIndex}
          onSelect={(index) => onActiveTabIndexChange(index)}
        >
          <EcTabList>
            <EcTab>
              <Text variant="m-dense-bold">
                <FormattedMessage
                  id="TicketsViewPage.workflowTab"
                  defaultMessage="Workflow"
                  description="Label for the Workflow tab."
                />
              </Text>
            </EcTab>
          </EcTabList>
          <EcTabPanel>
            <FlexLayout space={6}>
              <FlexLayout flexDirection="column" flexGrow="1" space={12}>
                <ContentContainer
                  loadingContent={{
                    isLoading: isDocumentSectionLoading,
                    message: intl.formatMessage({
                      id: 'TicketsViewPage.loadingDocumentInfo',
                      defaultMessage: 'Loading document information',
                      description: 'Loading message for document information.',
                    }),
                  }}
                >
                  <DocumentSection
                    ticket={ticket}
                    isEsignatureV2Enabled={isEsignatureV2Enabled}
                    ticketId={ticketId}
                    hasTicketInboundEmail={hasTicketInboundEmail}
                    documentActionsHandlers={documentActionsHandlers}
                    documentActionsLoaders={documentActionsLoaders}
                    pilotDocHandlerId={pilotDocHandlerId}
                  />
                </ContentContainer>
                <ContentContainer
                  loadingContent={{
                    isLoading: isLoadingCurrentStage,
                    message: intl.formatMessage({
                      id: 'TicketsViewPage.loadingStagesInfo',
                      defaultMessage: 'Loading stages information',
                      description: 'Loading message for stages information.',
                    }),
                  }}
                >
                  <StageContent
                    ticket={ticket}
                    hasTemporaryViewAnyPermission={
                      hasTemporaryViewAnyPermission
                    }
                    hasEsignatureV2Revamp={hasEsignatureV2Revamp}
                    envelope={envelope}
                    isLoadingEnvelope={isLoadingEnvelope}
                  />
                </ContentContainer>
              </FlexLayout>
              <Box sx={{ width: '280px' }}>
                <TicketSidebar
                  ticketId={ticket.id}
                  turnTrackingSidebar={
                    <TurnTrackingSidebar
                      turnTrackingHistoryData={turnTrackingHistoryData}
                      turnTrackingPartiesData={turnTrackingPartiesData}
                      isLoadingTurnTrackingHistory={
                        isLoadingTurnTrackingHistory
                      }
                      isLoadingTurnTrackingParties={
                        isLoadingTurnTrackingParties
                      }
                      isLoadingPermission={isLoadingPermission}
                      onViewCycleTimeTracker={onViewCycleTimeTracker}
                    />
                  }
                  hasTicketInboundEmail={hasTicketInboundEmail}
                  inboundEmailAddress={inboundEmailAddress}
                />
              </Box>
            </FlexLayout>
          </EcTabPanel>
        </EcTabs>
      </AccessControl>
      {addParticipantModal}
    </AppLayout>
  );
}

function AskAiStatusMessage({
  ticketActivities,
  versionId,
  isChatBotPanelVisible,
}) {
  const intl = useIntl();
  const isEdited = ticketActivities?.some(
    (activity) =>
      activity.action === TicketActivityType.DocumentEdit &&
      activity.data?.version?.id === versionId,
  );

  if (!isEdited || !isChatBotPanelVisible) {
    return null;
  }

  return (
    <BoxEds mb={4} w="50%">
      <StatusMessage
        message={intl.formatMessage({
          id: 'TicketsViewPage.askAiStatusMessage',
          defaultMessage:
            'Questions are answered based upon the original document text, with all redlines accepted. This document has edits that have not been saved to a new version. Please save as new version before asking questions.',
          description:
            'Status message for Ask AI when document has unsaved edits.',
        })}
        status="active"
      />
    </BoxEds>
  );
}

function TurnTrackingSidebar({
  turnTrackingHistoryData,
  turnTrackingPartiesData,
  isLoadingTurnTrackingHistory,
  isLoadingTurnTrackingParties,
  isLoadingPermission,
  onViewCycleTimeTracker,
}) {
  const currentPartyId = turnTrackingHistoryData[0]?.partyId;
  const currentPartyName = partyNameFromPartyIdAndParties(
    currentPartyId,
    turnTrackingPartiesData,
  );

  return (
    <BoxEds border="border" borderRadius="m" p={2} mb={2}>
      {isLoadingTurnTrackingHistory ||
      isLoadingTurnTrackingParties ||
      isLoadingPermission ? (
        <Layout h={40}>
          <LoadingShimmer />
        </Layout>
      ) : (
        <Layout direction="column">
          <TextEds id="currently-with" variant="tiny">
            <FormattedMessage
              id="TicketsViewPage.currentlyWith"
              defaultMessage="Currently with:"
              description="Label for the current person in turn tracking"
            />
          </TextEds>
          <TextEds aria-labelledby="currently-with" variant="body-bold">
            {currentPartyName ?? (
              <FormattedMessage
                id="TicketsViewPage.notSet"
                defaultMessage="Not Set"
                description="Default text when a value is not set"
              />
            )}
          </TextEds>
          <Link variant="tiny" onClick={onViewCycleTimeTracker}>
            <FormattedMessage
              id="TicketsViewPage.viewCycleTimeTracker"
              defaultMessage="View cycle time tracker"
              description="Link text to view the cycle time tracker"
            />
          </Link>
        </Layout>
      )}
    </BoxEds>
  );
}

function TicketSidebar({
  ticketId,
  turnTrackingSidebar,
  hasTicketInboundEmail,
  inboundEmailAddress,
}) {
  return (
    <>
      {turnTrackingSidebar}
      <TicketParticipants ticketId={ticketId} />
      {hasTicketInboundEmail && (
        <>
          <Divider />
          <TicketInboundEmail ticketEmail={inboundEmailAddress} />
        </>
      )}
    </>
  );
}

function DocumentSection({
  ticket,
  isEsignatureV2Enabled,
  ticketId,
  hasTicketInboundEmail,
  documentActionsHandlers,
  documentActionsLoaders,
  pilotDocHandlerId,
}) {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { navigate } = useRouting();
  const { document, documentVersions, permissions: userPermissions } = ticket;
  const hasWSSIntegrationM4SupplierNegotiation = useFlag(
    FlagType.WSSIntegrationM4SupplierNegotiation,
  );

  const isDocumentLinkDisabledByNegotiation =
    hasWSSIntegrationM4SupplierNegotiation &&
    Boolean(ticket.negotiationStatus) &&
    ticket.negotiationStatus === TicketNegotiationStatus.InProgress;

  const currentVersion = documentVersions.find(
    (d) => d.versionNumber === document.version,
  );
  const currentVersionFileType = currentVersion?.fileType;
  const documentName = currentVersion?.name ?? document.name;
  const currentVersionDummyFile = new File(
    [],
    `file.${currentVersionFileType}`,
  );

  return (
    <FlexLayout
      flexDirection="column"
      bg="white"
      p={6}
      space={6}
      sx={{ border: 'border', borderRadius: 'm', boxShadow: 'depth-1' }}
    >
      <FlexLayout alignItems="center" space={2}>
        <Icon icon={getFileIcon(currentVersionDummyFile)} size="xl" />
        <FlexLayout alignItems="flex-start" flexDirection="column" space={2}>
          <AccessControl
            userPermissions={userPermissions}
            requiredPermission={TicketPermissionType.Reviewer}
            renderNoAccess={() => (
              <Tooltip
                tooltip={intl.formatMessage({
                  id: 'TicketsViewPage.noViewPermissionTooltip',
                  defaultMessage:
                    'You do not have permissions to view this document.',
                  description: 'Tooltip for when user cannot view a document.',
                })}
              >
                <FlexLayout alignItems="center" space={1}>
                  <TruncateText maxW={500} variant="body-bold" disabled>
                    {removeFileNameExtension(
                      documentName,
                      currentVersionFileType,
                    )}
                  </TruncateText>
                  <Icon icon="lock" color="gray-600" size="xs" />
                </FlexLayout>
              </Tooltip>
            )}
          >
            {isDocumentLinkDisabledByNegotiation ? (
              <TruncateText
                maxW={500}
                variant="body-bold"
                data-testid="document-link"
              >
                {removeFileNameExtension(documentName, currentVersionFileType)}
              </TruncateText>
            ) : (
              <Link
                data-testid="document-link"
                pathname={`/workflow/tickets/${ticket.id}/reviewer/${document.version}`}
                variant="body-bold"
                maxW={500}
                shouldTruncate
              >
                {removeFileNameExtension(documentName, currentVersionFileType)}
              </Link>
            )}
          </AccessControl>
          <FlexLayout alignItems="center">
            <Text color="gray-600" variant="xs-dense">
              <FormattedMessage
                id="TicketsViewPage.version"
                defaultMessage="Version {version}"
                description="Displays the version number of a document."
                values={{ version: document.version }}
              />
              {document.modifiedDate &&
                (document.lastModifiedById || document.lastModifiedByEmail) && (
                  <LastModifiedBy
                    lastModifiedById={document.lastModifiedById}
                    lastModifiedByEmail={document.lastModifiedByEmail}
                    modifiedDate={document.modifiedDate}
                    hasTicketInboundEmail={hasTicketInboundEmail}
                    firstActivityAction={ticket.activities[0]?.['action']}
                  />
                )}
            </Text>
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
      {isEsignatureV2Enabled && ticket.stage === TicketStageType.Sign && (
        <TokenStatusMessage ticketId={ticketId} />
      )}
      {ticket.isCancelled ? (
        <TicketCancelledWarning />
      ) : (
        <>
          {ticket.stage === TicketStageType.Sign && <ESignatureSetupAlert />}
          <Divider />
          {ticket.stage === TicketStageType.Review && <TicketDocumentNotices />}
        </>
      )}
      {ticket.status !== TicketStatusType.Completed && !ticket.isCancelled && (
        <TicketDocumentActions
          handlers={documentActionsHandlers}
          loaders={documentActionsLoaders}
        />
      )}
      {ticket.status === TicketStatusType.Completed && (
        <FlexLayout flexDirection="row" space={2}>
          {!checkIsWorkflowOnly(currentUser) && (
            <FlexLayout flexDirection="column" space={1}>
              <Box>
                <Button
                  text={intl.formatMessage({
                    id: 'TicketsViewPage.viewCompletedDocument',
                    defaultMessage: 'View Completed Document',
                    description: 'Button text to view a completed document.',
                  })}
                  disabled={pilotDocHandlerId === null}
                  onClick={() => navigate(`/document/${pilotDocHandlerId}`)}
                  variant="secondary"
                />
              </Box>
              {!ticket.pilotDocHandlerId && (
                <Text color="gray-600" variant="xs-dense">
                  <FormattedMessage
                    id="TicketsViewPage.waitForDocument"
                    defaultMessage="Please wait for 2 minutes then refresh the page to see the document."
                    description="Message to user to wait for document to be available."
                  />
                </Text>
              )}
            </FlexLayout>
          )}
        </FlexLayout>
      )}
    </FlexLayout>
  );
}

function ReviewSection({ ticket }) {
  const currentUser = useCurrentUser();
  const { stage, stages, approvals } = ticket;
  const { phaseIds, coordinatorId, id } = stages.review;

  const nonEmptyPhaseIds = phaseIds.filter((phaseId) => {
    const phase = ticket.phases[phaseId];
    return testPhaseNonEmpty(phase);
  });

  const allApprovalsNeutral = !Object.values(approvals)
    .filter((approval) => nonEmptyPhaseIds.includes(approval.phaseId))
    .some((approval) => approval.status !== TicketStatusType.Pending);

  return (
    <FlexLayout flexDirection="column" space={4}>
      <Text variant="xl-dense-bold">
        <FormattedMessage
          id="TicketsViewPage.review"
          defaultMessage="Review"
          description="Title for the review section."
        />
      </Text>
      {coordinatorId && (
        <FlexLayout flexDirection="column" space={3}>
          <Text color="gray-600" variant="xs-dense-caps">
            <FormattedMessage
              id="TicketsViewPage.reviewCoordinator"
              defaultMessage="Review Coordinator"
              description="Label for the review coordinator."
            />
          </Text>
          <TicketUserItem
            issueId={id}
            userPermissions={ticket.permissions}
            stageId={id}
            ticketId={ticket.id}
            userId={coordinatorId}
            currentUserId={currentUser.id}
            allItemsNeutral={allApprovalsNeutral}
            isCoordinator={true}
          />
        </FlexLayout>
      )}
      <Box>
        {nonEmptyPhaseIds.map((phaseId, index) => (
          <TicketReviewPhase
            key={phaseId}
            phaseId={phaseId}
            previousPhaseId={nonEmptyPhaseIds[index - 1]}
            stage={stage}
          />
        ))}
      </Box>
    </FlexLayout>
  );
}

function SignaturesSection({
  ticket,
  hasEsignatureV2Revamp,
  envelope,
  isLoadingEnvelope,
}) {
  const currentUser = useCurrentUser();
  const { stage, stages, approvals } = ticket;
  const { phaseIds, coordinatorId, id } = stages.sign;

  const nonEmptyPhaseIds = phaseIds.filter((phaseId) => {
    const phase = ticket.phases[phaseId];
    return (phase.judgmentIds ?? []).length > 0;
  });

  const allSignaturesNeutral = !Object.values(approvals)
    .filter((approval) => phaseIds.includes(approval.phaseId))
    .some(
      (approval) =>
        approval.status !== TicketStatusType.SignatureRequestNotSent,
    );

  let recipientsComponent = nonEmptyPhaseIds.map((phaseId, index) => (
    <TicketReviewPhase
      key={phaseId}
      phaseId={phaseId}
      previousPhaseId={phaseIds[index - 1]}
      stage={stage}
    />
  ));

  if (hasEsignatureV2Revamp) {
    recipientsComponent = <TicketRecipients />;
  }

  if (!hasEsignatureV2Revamp && nonEmptyPhaseIds.length) {
    const phasesSubset = Object.fromEntries(
      nonEmptyPhaseIds.map((phaseId) => [phaseId, ticket.phases[phaseId]]),
    );
    recipientsComponent = (
      <TicketSigningPhase phases={phasesSubset} phaseId={phaseIds[0]} />
    );
  }

  return (
    <FlexLayout flexDirection="column" space={4}>
      <FlexLayout flexDirection="row" alignItems="center" space={2}>
        <Text variant="xl-dense-bold">
          <FormattedMessage
            id="TicketsViewPage.signatures"
            defaultMessage="Signatures"
            description="Title for the signatures section."
          />
        </Text>
        {hasEsignatureV2Revamp && (envelope || isLoadingEnvelope) && (
          <EnvelopeStatusChip
            envelope={envelope}
            isLoading={isLoadingEnvelope}
          />
        )}
      </FlexLayout>
      <FlexLayout flexDirection="column" space={3}>
        <Text color="gray-600" variant="xs-dense-caps">
          <FormattedMessage
            id="TicketsViewPage.signingCoordinator"
            defaultMessage="Signing Coordinator"
            description="Label for the signing coordinator."
          />
        </Text>
        <TicketUserItem
          issueId={id}
          userPermissions={ticket.permissions}
          stageId={id}
          ticketId={ticket.id}
          userId={coordinatorId}
          currentUserId={currentUser.id}
          allItemsNeutral={allSignaturesNeutral}
          isCoordinator={true}
        />
      </FlexLayout>
      {recipientsComponent}
    </FlexLayout>
  );
}

function ChecklistSection({ ticket }) {
  const currentUser = useCurrentUser();
  const { stage, stages, approvals } = ticket;
  const { phaseIds, coordinatorId, id } = stages.finalize;

  const allTasksNeutral = !Object.values(approvals)
    .filter((approval) => phaseIds.includes(approval.phaseId))
    .some((approval) => approval.status !== TicketStatusType.Pending);

  const nonEmptyPhaseIds = phaseIds.filter((phaseId) => {
    const phase = ticket.phases[phaseId];
    return (phase.judgmentIds ?? []).length > 0;
  });

  return (
    <FlexLayout flexDirection="column" space={4}>
      <Text variant="xl-dense-bold">
        <FormattedMessage
          id="TicketsViewPage.checklist"
          defaultMessage="Checklist"
          description="Title for the checklist section."
        />
      </Text>
      <FlexLayout flexDirection="column" space={3}>
        <Text color="gray-600" variant="xs-dense-caps">
          <FormattedMessage
            id="TicketsViewPage.checklistCoordinator"
            defaultMessage="Checklist Coordinator"
            description="Label for the checklist coordinator."
          />
        </Text>
        <TicketUserItem
          issueId={id}
          userPermissions={ticket.permissions}
          stageId={id}
          ticketId={ticket.id}
          userId={coordinatorId}
          currentUserId={currentUser.id}
          allItemsNeutral={allTasksNeutral}
          isCoordinator={true}
        />
      </FlexLayout>
      <Box>
        {nonEmptyPhaseIds.map((phaseId, index) => (
          <TicketReviewPhase
            key={phaseId}
            phaseId={phaseId}
            previousPhaseId={nonEmptyPhaseIds[index - 1]}
            stage={stage}
          />
        ))}
      </Box>
    </FlexLayout>
  );
}

function StageContent({
  ticket,
  hasTemporaryViewAnyPermission,
  hasEsignatureV2Revamp,
  envelope,
  isLoadingEnvelope,
}) {
  const { stages, stage } = ticket;
  if (!stages[stage]) {
    return null;
  }

  if (!hasTemporaryViewAnyPermission) {
    return null;
  }

  switch (stage) {
    case TicketStageType.Review:
      return <ReviewSection ticket={ticket} />;
    case TicketStageType.Sign:
      return (
        <SignaturesSection
          ticket={ticket}
          hasEsignatureV2Revamp={hasEsignatureV2Revamp}
          envelope={envelope}
          isLoadingEnvelope={isLoadingEnvelope}
        />
      );
    case TicketStageType.Finalize:
      return <ChecklistSection ticket={ticket} />;
    default:
      return null;
  }
}

const TicketsViewPage = () => {
  const intl = useIntl();
  const { params, navigate } = useRouting();
  const currentUser = useCurrentUser();
  const dispatch = useAppDispatch();
  const ticket = useAppSelector((state) => state.ticket);
  const hasTemporaryViewAnyPermission = useAppSelector((state) =>
    getHasTemporaryViewAnyPermission(state.ticket),
  );
  const { panels } = usePanels();
  const {
    isPanelEnlarged,
    togglePanelEnlarged,
    condensePanel,
    enlargeContent,
    condenseContent,
    isSidebarExpanded,
    openSidebar,
    closeSidebar,
  } = useAppConfigContext();
  const [error, setError] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(WORKFLOW_TAB_INDEX);
  const [isLoadingStage, setIsLoadingStage] = useState(false);
  const [isLoadingTicket, setIsLoadingTicket] = useState(true);
  const [isLoadingDocument, setIsLoadingDocument] = useState(true);
  const [isLoadingCurrentStage, setIsLoadingCurrentStage] = useState(true);
  const [selectedPanel, setSelectedPanel] = useState('');
  const [editorKeyRequestCount, setEditorKeyRequestCounter] = useState(0);
  const [ticketDocumentVersionEditorKey, setTicketDocumentVersionEditorKey] =
    useState('');
  const [hasEditorKeyPollingFinished, setHasEditorKeyPollingFinished] =
    useState(false);
  const { ticketId } = params;
  const errorInQuery = getPageSearchQueryByKey(QueryParamType.Error);
  const isEsignatureV2Enabled = useFlag(FlagType.EsignatureV2Revamp);

  const isMultiDocEnabled = useFlag(
    FlagType.MultiDocWorkflowSupportAppendDocumentToContract,
  );
  const updateActivityLog = useActivityLogUpdater({ ticketId });
  const { hasPermission: hasAskAIPermission, isLoading: isLoadingPermission } =
    usePermission({
      permission: {
        resourceId: 'conversational_ai',
        resourceType: 'edit',
      },
    });
  const { toast } = useToast();

  const defaultPanelActions = useMemo(
    () => [
      {
        icon: 'maximize',
        isActive: isPanelEnlarged,
        label: isPanelEnlarged
          ? intl.formatMessage({
              id: 'TicketsViewPage.condense',
              defaultMessage: 'Condense',
              description: 'Label for condensing the panel',
            })
          : intl.formatMessage({
              id: 'TicketsViewPage.enlarge',
              defaultMessage: 'Enlarge',
              description: 'Label for enlarging the panel',
            }),
        value: isPanelEnlarged ? 'Condense' : 'Enlarge',
        onClick: togglePanelEnlarged,
      },
    ],
    [intl, isPanelEnlarged, togglePanelEnlarged],
  );

  const panelWidth = isPanelEnlarged ? 'fill' : 'm';

  const { isVisible: isAskAISidepanelVisible, width: askAISidepanelWidth } =
    useAskAISidepanel();

  const isAskAISidepanelVisibleRef = useRef(isAskAISidepanelVisible);
  useEffect(() => {
    isAskAISidepanelVisibleRef.current = isAskAISidepanelVisible;
  }, [isAskAISidepanelVisible]);

  useEffect(() => {
    if (isAskAISidepanelVisible) {
      setSelectedPanel(PanelType.AskAI);
      openSidebar();
    } else if (selectedPanel === PanelType.AskAI && !isAskAISidepanelVisible) {
      closeSidebar();
    }
  }, [isAskAISidepanelVisible]);

  const onPanelClick = (_action) => {
    if (isSidebarExpanded) {
      condensePanel();
      closeSidebar();
      enlargeContent();
    } else {
      openSidebar();
      condenseContent();
    }
  };

  const {
    data: ticketDocumentData = {},
    isSuccess: isTicketDocumentSuccess,
    fulfilledTimeStamp: ticketDocumentFulfilledTimeStamp,
  } = api.endpoints.getTicketDocument.useQuery({
    ticketId,
  });

  const {
    data: ticketCurrentStageData = {},
    isSuccess: isTicketCurrentStageSuccess,
    fulfilledTimeStamp: ticketCurrentStageFulfilledTimeStamp,
  } = api.endpoints.getTicketCurrentStage.useQuery({ ticketId });

  const {
    isError: isTicketSummaryError,
    error: ticketSummaryError,
    data: ticketSummaryData = {},
    isSuccess: isTicketSummarySuccess,
    fulfilledTimeStamp: ticketSummaryFulfilledTimeStamp,
  } = api.endpoints.getTicketSummary.useQuery({
    ticketId,
  });

  const {
    data: turnTrackingHistoryData = [],
    isError: turnTrackingHistoryError,
    isLoading: isLoadingTurnTrackingHistory,
    fulfilledTimeStamp: turnTrackingHistoryFulfilledTimeStamp,
  } = api.endpoints.getTurnTrackingHistory.useQuery(
    {
      ticketId,
    },
    { skip: !ticketId },
  );

  const { data: envelope, isLoading: isLoadingEnvelope } =
    api.endpoints.getEnvelope.useQuery(
      {
        ticketId,
      },
      {
        skip: !isEsignatureV2Enabled || ticket.stage !== TicketStageType.Sign,
      },
    );

  const {
    data: turnTrackingPartiesDataRaw = [],
    isError: turnTrackingPartiesError,
    isLoading: isLoadingTurnTrackingParties,
  } = api.endpoints.getTurnTrackingParties.useQuery(
    {
      workflowId: ticket.workflowId,
    },
    { skip: !ticket.workflowId },
  );
  const turnTrackingPartiesNames = getTurnTrackingPartiesNames(intl);
  const turnTrackingPartiesData = turnTrackingPartiesDataRaw.map((party) => ({
    ...party,
    name: turnTrackingPartiesNames[party.name],
  }));

  const { document, inboundEmailAddress } = ticket;
  const { id: documentId } = document;

  const version = getDocumentVersion(ticket, params.versionNumber);
  const versionId = version?.id;
  const isWorkingDraft = version.hasWorkingDraft;
  const pilotDocHandlerId = ticket.pilotDocHandlerId;

  const skipEditorKeyRequest =
    !isWorkingDraft ||
    editorKeyRequestCount >= TICKET_DOCUMENT_VERSION_POLLING_MAX_COUNTER ||
    hasEditorKeyPollingFinished;

  const tryAgainLaterMessage = intl.formatMessage({
    id: 'TicketsViewPage.tryAgainLater',
    defaultMessage: 'Try again in a few minutes.',
    description: 'Toast message to suggest trying again later',
  });
  const { data: editorKeyData, requestId } =
    api.endpoints.getTicketDocumentVersionEditorKey.useQuery(
      { documentId, versionId },
      {
        pollingInterval: TICKET_DOCUMENT_VERSION_EDITOR_KEY_POLLING_INTERVAL,
        skip: skipEditorKeyRequest,
      },
    );

  const handleTicketError = () => {
    showToast(
      toastTypes.ERROR,
      intl.formatMessage({
        id: 'TicketsViewPage.loadTicketError',
        defaultMessage: 'There has been an error when loading this ticket.',
        description: 'Error message for when loading a ticket fails.',
      }),
    );
    navigate('/workflow/tickets');
  };

  const fetchActivityLog = useCallback(async () => {
    const activities = await getActivities({
      entityId: ticketId,
      entityType: EntityType.Ticket,
    });
    dispatch(ticketActivitiesSet(activities));
  }, [ticketId, dispatch]);

  // onSuccess for the ticketDocument fetch
  useEffect(() => {
    if (isTicketDocumentSuccess) {
      try {
        dispatch(ticketDocumentSet(ticketDocumentData));
      } catch {
        handleTicketError();
      } finally {
        setIsLoadingDocument(false);
      }
    }
  }, [ticketDocumentFulfilledTimeStamp]);

  // onSuccess for the ticketSummary fetch
  useEffect(() => {
    if (isTicketSummarySuccess) {
      try {
        dispatch(ticketSummarySet(ticketSummaryData));
      } catch {
        handleTicketError();
      } finally {
        setIsLoadingTicket(false);
        fetchActivityLog();
      }
    }
  }, [ticketSummaryFulfilledTimeStamp]);

  // onSuccess for the ticketCurrentStage fetch
  useEffect(() => {
    if (isTicketCurrentStageSuccess) {
      try {
        dispatch(ticketStagesSet(ticketCurrentStageData));
      } catch {
        handleTicketError();
      } finally {
        setIsLoadingCurrentStage(false);
      }
    }
  }, [ticketCurrentStageFulfilledTimeStamp]);

  useEffect(() => {
    if (turnTrackingHistoryData) {
      dispatch(slices.turnTracking.actions.setHistory(turnTrackingHistoryData));
    }
  }, [turnTrackingHistoryFulfilledTimeStamp]);

  useEffect(() => {
    if (turnTrackingPartiesError) {
      toast({
        status: 'info',
        title: intl.formatMessage({
          id: 'TicketsViewPage.loadingResponsiblePartiesErrorTitle',
          defaultMessage:
            "We're experiencing difficulty loading Responsible Parties",
          description: 'Toast title for error when loading responsible parties',
        }),
        message: tryAgainLaterMessage,
      });
    }
  }, [turnTrackingPartiesError]);

  useEffect(() => {
    if (turnTrackingHistoryError) {
      toast({
        status: 'info',
        title: intl.formatMessage({
          id: 'TicketsViewPage.loadingCycleTimeHistoryErrorTitle',
          defaultMessage:
            "We're experiencing difficulty loading Cycle Time History",
          description: 'Toast title for error when loading cycle time history',
        }),
        message: tryAgainLaterMessage,
      });
    }
  }, [turnTrackingHistoryError]);

  const hasTicketInboundEmail = useHasFeatureFlag(
    FeatureFlagType.TicketInboundEmail,
  );
  const hasEsignatureV2Revamp = useFlag(FlagType.EsignatureV2Revamp);
  const isAskAnythingEnabled = useFlag(FlagType.AskAnything);
  const isAskAnythingPreSigEnabled =
    useFlag(FlagType.AskAnythingPreSig) &&
    isAskAnythingEnabled &&
    hasAskAIPermission;

  const [ticketCommentId] = useSearchParams(QueryParamType.TicketCommentId);
  const [activeTab] = useSearchParams(QueryParamType.ActiveTab);
  const ticketEntity = { id: ticketId, type: EntityType.Ticket };

  const [addParticipantModal, openAddParticipantModal] =
    useAddTicketParticipantModal({
      ticketId,
    });

  useEffect(() => {
    if (activeTab) {
      setActiveTabIndex(parseInt(activeTab));
    }
  }, [activeTab]);

  useEffect(() => {
    if (editorKeyData) {
      if (
        ticketDocumentVersionEditorKey &&
        editorKeyData.key !== ticketDocumentVersionEditorKey
      ) {
        setHasEditorKeyPollingFinished(true);
      }
      setTicketDocumentVersionEditorKey(editorKeyData.key);
    }
  }, [editorKeyData]);

  useEffect(() => {
    if (requestId) {
      setEditorKeyRequestCounter(editorKeyRequestCount + 1);
    }
  }, [requestId]);

  useEffect(() => {
    if (errorInQuery) {
      showToast(
        toastTypes.ERROR,
        intl.formatMessage({
          id: 'TicketsViewPage.eSignatureLoginFailed',
          defaultMessage:
            'Login into e-signature provider failed. Please try again.',
          description: 'Toast message for e-signature login failure',
        }),
      );
    }
  }, [errorInQuery]);

  useEffect(() => {
    return () => {
      dispatch(ticketReset());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isTicketSummaryError) {
      if (ticketSummaryError.response?.status) {
        setError(ticketSummaryError.response?.status);
        setIsLoadingTicket(false);
      }
    }
  }, [isTicketSummaryError, ticketSummaryError]);

  const { data: documentData } = api.endpoints.getDocumentByTag.useQuery(
    { tag: versionId },
    {
      skip: !isAskAnythingPreSigEnabled || !versionId,
    },
  );

  const [postTicketNextStage] = api.endpoints.postTicketNextStage.useMutation();

  useEffect(() => {
    if (documentData && documentData.currentVersion) {
      const { currentVersion, id } = documentData;
      // format the name to be "(v1) My document"
      const parsedFileName = `(v${
        version?.versionNumber
      }) ${removeFileNameExtension(
        currentVersion.fileName,
        version?.fileType,
      )}`;
      const isEdited = ticket.activities?.some(
        (activity) =>
          activity.action === TicketActivityType.DocumentEdit &&
          activity.data?.version?.id === versionId,
      );
      dispatch(
        actions.setCurrentDocument({
          id,
          versionId: currentVersion.id,
          name: parsedFileName,
          type: currentVersion.fileType,
          ticketVersionId: versionId,
          folder: '',
          isEdited,
        }),
      );
    }

    return () => {
      dispatch(actions.setCurrentDocument(null));
    };
  }, [documentData, ticket.activities]);

  const toggleAskAI = () => {
    if (isAskAISidepanelVisible) {
      dispatch(slices.chatbot.actions.closeChatbot());
      closeSidebar();
    } else {
      dispatch(slices.chatbot.actions.toggleChatbot());
    }
  };

  useAsync(
    checkESignatureLogin,
    { ticketId, redirectUri: window.location.href },
    {
      condition: isEsignatureV2Enabled
        ? false
        : ticket.stage === TicketStageType.Sign,
      deps: [ticketId, ticket.stage],
      successHandler: async () => {
        let hasEnvelope = false;
        try {
          hasEnvelope = (await checkEnvelope(ticketId)).data;
          if (!hasEnvelope && ticket.stage === TicketStageType.Sign) {
            await updateEnvelope(ticketId);
          }
        } catch (error) {
          if (
            error.response?.status === HttpStatusCodeType.UnprocessableContent
          ) {
            const { errors } = error.response.data;
            const er = errors[0];
            toast({
              autoDismiss: false,
              message: er?.detail,
              status: 'warning',
            });
          }
        } finally {
          const hasEsignatureIntegration = await checkEsignatureIntegration();
          dispatch(ticketUpdate({ hasEsignatureIntegration, hasEnvelope }));
        }
      },
      errorToastMessage: intl.formatMessage({
        id: 'TicketsViewPage.eSignatureProviderError',
        defaultMessage:
          'There was an issue communicating with the e-signature provider. Please try again.',
        description:
          'Error message for e-signature provider communication issue.',
      }),
    },
  );

  useAsync(getTicketAttachments, ticketId, {
    condition: ticketId,
    deps: [ticketId],
    successHandler: (response) => {
      dispatch(ticketAttachmentsSet(response));
    },
    errorToastMessage: intl.formatMessage({
      id: 'TicketsViewPage.loadAttachmentsError',
      defaultMessage:
        'There was an error loading the ticket attachments. Please try again.',
      description: 'Error message for loading ticket attachments.',
    }),
  });

  const { isLoading: isLoadingDocumentVersions } = useAsync(
    getTicketDocumentVersions,
    documentId,
    {
      condition: documentId,
      deps: [documentId],
      successHandler: (response) => {
        dispatch(ticketDocumentVersionsSet(response));
      },
      errorToastMessage: intl.formatMessage({
        id: 'TicketsViewPage.loadDocumentVersionsError',
        defaultMessage:
          'There was an error loading the document versions. Please try again.',
        description: 'Error message for loading document versions.',
      }),
    },
  );

  const getDocumentVersions = async (documentId) => {
    const upToDateVersions = await getTicketDocumentVersions(documentId);
    dispatch(ticketDocumentVersionsSet(upToDateVersions));
  };

  const handleNextStageClick = async () => {
    if (isMultiDocEnabled) {
      try {
        const res = await postTicketNextStage(ticketId).unwrap();
        getDocumentVersions(ticketDocumentData.id);
        nextStageSuccessHandler(res);
      } catch {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'TicketsViewPage.mergeError',
            defaultMessage:
              'There was an error merging the document versions. Please try again.',
            description: 'Error message for merging document versions.',
          }),
        );
      }
    } else {
      handleNextStage();
    }
  };

  const nextStageSuccessHandler = async (res) => {
    showToast(
      toastTypes.SUCCESS,
      intl.formatMessage({
        id: 'TicketsViewPage.stageCompletedSuccess',
        defaultMessage: 'Successfully completed stage',
        description: 'Success message for completing a stage.',
      }),
    );
    try {
      // we are changing stages, so next stage is not ready yet.
      setIsLoadingStage(true);
      const participants = await getTicketParticipants({ ticketId });
      const tags = [TAGS.ACL_TAG, TAGS.TAG_BY_TICKET_SUMMARY];
      if (res.stage === TicketStageType.Sign) {
        tags.push(
          {
            type: TagType.EsignatureEnvelope,
            id: ticketId,
          },
          {
            type: TagType.TicketRecipients,
            id: ticketId,
          },
        );
      }
      dispatch(api.util.invalidateTags(tags));
      await updateActivityLog();
      dispatch(
        ticketUpdate({
          stages: res.stages,
        }),
      );

      dispatch(ticketParticipantsSet(participants.data));
      setIsLoadingStage(false);
    } catch {
      window.location.reload();
    }
  };

  const { executor: handleNextStage, isLoading: isCompletingStage } = useAsync(
    patchTicketStage,
    { ticketId },
    {
      successHandler: async (res) => {
        nextStageSuccessHandler(res);
      },
      errorToast: intl.formatMessage({
        id: 'TicketsViewPage.nextStageTransitionFailed',
        defaultMessage:
          'Transition to the next stage failed. Please refresh the page and try again.',
        description: 'Error message for next stage transition failure.',
      }),
    },
  );

  const { executor: handleOnDownloadClick, isLoading: isLoadingDownload } =
    useAsync(
      downloadTicketDocument,
      { documentId, versionId, isWorkingDraft },
      {
        successToastMessage: intl.formatMessage(
          {
            id: 'TicketsViewPage.downloadingMessage',
            defaultMessage: '"{fileName}" is now downloading…',
            description:
              'Toast message for when a document download begins. Example: fileName = "contract.pdf"',
          },
          { fileName: ticket.name },
        ),
        successToastType: toastTypes.DOWNLOAD,
        successHandler: async (file) => {
          const fileType =
            file.type === FileMimeType.DocxUnofficial
              ? 'docx'
              : version.fileType;
          downloadFile(
            file,
            `${getBasename(document.name)}_${document.version}${
              isWorkingDraft ? '_draft' : ''
            }.${fileType}`,
          );
          await updateActivityLog();
        },
        errorToastMessage: intl.formatMessage({
          id: 'TicketsViewPage.downloadError',
          defaultMessage:
            'There has been an error with downloading this document. Please try again.',
          description: 'Error message for document download failure.',
        }),
      },
    );

  const isChatBotPanelVisible = useAppSelector(
    slices.chatbot.selectors.selectIsChatbotVisible,
  );
  async function uploadTicketDocVersion() {
    const documentBlob = await downloadTicketDocumentWithoutActivityLog({
      documentId,
      versionId,
      isWorkingDraft,
    });
    const file = new File(
      [documentBlob],
      `${getBasename(document.name)}.${version.fileType}`,
    );
    const res = await uploadNewDocumentVersion({
      ticketId,
      documentId,
      text: intl.formatMessage({
        id: 'TicketsViewPage.sendForSigning',
        defaultMessage: 'Send for signing',
        description: 'Text for send for signing action.',
      }),
      file,
      isCounterparty: false,
    });
    dispatch(
      // push the new version to redux
      ticketDocumentVersionsSet([
        { ...res, fileType: coerceFileType(res.fileType) },
        ...ticket.documentVersions,
      ]),
    );
    dispatch(api.util.invalidateTags([TAGS.TAG_CURRENT_STAGE]));
  }

  const {
    executor: handleSendForSigningExecutor,
    isLoading: isLoadingNewVersion,
  } = useAsync(
    uploadTicketDocVersion,
    {},
    {
      successToastMessage: intl.formatMessage({
        id: 'TicketsViewPage.newVersionSuccess',
        defaultMessage: 'Successfully created a new document version',
        description: 'Success message for creating a new document version.',
      }),
      successHandler: async () => {
        handleNextStageClick();
      },
      errorToastMessage: intl.formatMessage({
        id: 'TicketsViewPage.newVersionError',
        defaultMessage:
          'An error occurred when creating a new document version to send for signing.',
        description: 'Error message for creating a new document version.',
      }),
    },
  );

  const handleSendForSigning = async () => {
    if (isWorkingDraft) {
      handleSendForSigningExecutor();
    } else {
      handleNextStageClick();
    }
  };

  function handleOnAddSignersClick() {
    setSelectedPanel(PanelType.IntakeForm);
    openSidebar();
  }

  function onMentionRequestPermission(mentionedEntity) {
    const { type } = mentionedEntity;
    const entity = mentionedEntity.data[type];
    if (entity && type === EntityType.User && mentionedEntity.isInvalid) {
      openAddParticipantModal({ type: 'user', value: entity });
    } else if (entity && type === EntityType.Department) {
      openAddParticipantModal({ type: 'department', value: entity });
    }
  }

  function onCopyLink(comment) {
    const linkConstructed = `${window.location.origin}${window.location.pathname}?${QueryParamType.TicketCommentId}=${comment.id}`;
    copyToClipboard(linkConstructed, undefined, undefined, undefined, false);
  }

  useEffect(() => {
    if (ticketCommentId) {
      setSelectedPanel(PanelType.Comments);
      openSidebar();
    }
  }, [ticketCommentId]);

  useEffect(() => {
    if (!isAskAISidepanelVisible && !ticketCommentId) {
      setSelectedPanel('');
      closeSidebar();
      enlargeContent();
    }

    return () => {
      if (!isAskAISidepanelVisibleRef.current) closeSidebar();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const viewStagePermission =
    ticket.stage === TicketStageType.Finalize &&
    ticket.status === TicketStatusType.Completed
      ? TicketPermissionType.CompletedView
      : stagePermissions[ticket.stage];

  const isDocumentSectionLoading =
    isLoadingDocument ||
    isLoadingDocumentVersions ||
    ticket.documentVersions.length === 0 ||
    isLoadingCurrentStage;

  const documentActionsHandlers = {
    handleOnDownloadClick,
    handleOnShareSuccess: updateActivityLog,
    handleNextStageClick,
    handleOnAddSignersClick,
    handleSendForSigning,
  };
  const documentActionsLoaders = {
    isLoadingDownload,
    isPreparingDownload: !skipEditorKeyRequest,
    isLoadingNextStage: isCompletingStage || isLoadingStage,
    isLoadingSendForSigning:
      isLoadingNewVersion || isCompletingStage || isLoadingStage,
  };

  if (isLoadingTicket) {
    return <LoadingContainer />;
  }

  if (error) {
    return (
      <ErrorMessage
        ticketSummaryError={ticketSummaryError}
        onTakeMeToTickets={() => navigate('/workflow/tickets')}
      />
    );
  }

  return (
    <TicketContent
      ticket={ticket}
      viewStagePermission={viewStagePermission}
      isDocumentSectionLoading={isDocumentSectionLoading}
      isLoadingCurrentStage={isLoadingCurrentStage}
      isAskAnythingPreSigEnabled={isAskAnythingPreSigEnabled}
      versionId={versionId}
      isChatBotPanelVisible={isChatBotPanelVisible}
      turnTrackingHistoryData={turnTrackingHistoryData}
      turnTrackingPartiesData={turnTrackingPartiesData}
      isLoadingTurnTrackingHistory={isLoadingTurnTrackingHistory}
      isLoadingTurnTrackingParties={isLoadingTurnTrackingParties}
      isLoadingPermission={isLoadingPermission}
      onViewCycleTimeTracker={() => {
        setSelectedPanel(PanelType.CycleTimeTracker);
        openSidebar();
        condenseContent();
      }}
      hasTicketInboundEmail={hasTicketInboundEmail}
      inboundEmailAddress={inboundEmailAddress}
      isEsignatureV2Enabled={isEsignatureV2Enabled}
      documentActionsHandlers={documentActionsHandlers}
      documentActionsLoaders={documentActionsLoaders}
      pilotDocHandlerId={pilotDocHandlerId}
      hasTemporaryViewAnyPermission={hasTemporaryViewAnyPermission}
      hasEsignatureV2Revamp={hasEsignatureV2Revamp}
      envelope={envelope}
      isLoadingEnvelope={isLoadingEnvelope}
      defaultPanelActions={defaultPanelActions}
      panels={panels}
      onPanelClick={onPanelClick}
      documentData={documentData}
      ticketCommentId={ticketCommentId}
      ticketEntity={ticketEntity}
      panelWidth={panelWidth}
      onMentionRequestPermission={onMentionRequestPermission}
      onCopyLink={onCopyLink}
      ticketId={ticketId}
      documentVersions={ticket.documentVersions}
      isSidebarExpanded={isSidebarExpanded}
      isAskAISidepanelVisible={isAskAISidepanelVisible}
      askAISidepanelWidth={askAISidepanelWidth}
      selectedPanel={selectedPanel}
      onPanelUpdate={(panel) => {
        setSelectedPanel(panel);
        if (panel !== PanelType.AskAI) {
          dispatch(slices.chatbot.actions.closeChatbot());
        } else {
          toggleAskAI();
        }
      }}
      onSidebarClose={() => {
        if (selectedPanel === PanelType.AskAI) {
          dispatch(slices.chatbot.actions.closeChatbot());
        }
      }}
      addParticipantModal={addParticipantModal}
      activeTabIndex={activeTabIndex}
      onActiveTabIndexChange={setActiveTabIndex}
      isPanelEnlarged={isPanelEnlarged}
    />
  );
};

export default TicketsViewPage;
